"use client";

import Link from "next/link";
import { SiteHero, SectionHead, FullBleedPhoto, CtaBand, RelatedLinks } from "@/components/marketing/site-ui";
import { buttonClasses } from "@/lib/ui/buttonClasses";
import { useSiteContent } from "@/components/marketing/use-site-content";

/**
 * About.
 *
 * Deliberately not a founding story with invented milestones. What GrowthSync can honestly
 * put on an about page today is what it refuses to do and why, which is also the only part
 * a business owner deciding whether to trust it actually reads.
 */
export function AboutClient() {
  const c = useSiteContent();
  const page = c.aboutPage;

  return (
    <>
      <SiteHero
        eyebrow={page.eyebrow}
        title={page.title}
        lede={page.lede}
        primaryLabel={c.cta.primary}
        secondaryHref="/features"
        secondaryLabel={c.common.backToFeatures}
      />

      {/* The beliefs, as a numbered argument down the page rather than a card grid. Each one
          is a decision that cost something, so each gets its own line of the page. */}
      <section className="border-t border-neutral-200 bg-white py-16 md:py-24">
        <div className="gs-page">
          <SectionHead title={page.beliefsHeading} />
          <div className="mt-10 divide-y divide-neutral-200 border-y border-neutral-200">
            {page.beliefs.map((belief) => (
              <div key={belief.title} className="grid gap-3 py-8 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] md:gap-12">
                <h3 className="gs-title text-balance text-neutral-900">{belief.title}</h3>
                <p className="text-[15.5px] leading-relaxed text-neutral-600">{belief.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="gs-page">
          <SectionHead eyebrow={page.whoEyebrow} title={page.whoTitle} body={page.whoBody} />
          <div className="mt-8 flex flex-wrap gap-3">
            {[
              { href: "/for/founders", label: c.audiences.founders.name },
              { href: "/for/business-owners", label: c.audiences.owners.name },
            ].map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-bold text-neutral-800 transition-colors hover:border-primary/40 hover:text-primary"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FullBleedPhoto
        src="/images/discover-first-meeting.webp"
        alt={page.photoAlt}
        eyebrow={page.photoEyebrow}
        title={page.photoTitle}
        body={page.photoBody}
      />

      <section className="py-16 md:py-20">
        <div className="gs-page">
          <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-8 md:p-12">
            <h2 className="gs-title text-neutral-900">{page.companyHeading}</h2>
            <p className="mt-4 gs-measure text-[15.5px] leading-relaxed text-neutral-600">{page.companyBody}</p>
            <Link href="/contact" className={buttonClasses({ variant: "ghost", size: "md", className: "mt-7" })}>
              {page.contactCta}
            </Link>
          </div>
        </div>
      </section>

      <RelatedLinks
        links={[
          { href: "/safety", title: c.company.safety, body: c.company.safetyTagline },
          { href: "/faq", title: c.company.faq, body: c.company.faqTagline },
          { href: "/pricing", title: c.nav.pricing, body: c.pricingPage.short },
        ]}
      />

      <CtaBand />
    </>
  );
}
